import { useCallback, useEffect, useRef, useState } from 'react';
import type { GeniusFinishAudit, SubmitGeniusRoundResponse } from '../services/api';
import { decryptGeniusBootstrapCipher } from '../utils/geniusBootstrapDecrypt';
import {
  loadGeniusAssemblyExports,
  type GeniusWasmExports,
} from '../utils/geniusWasmExports';

export interface GeniusPlayStateDto {
  playSessionId: string;
  quizId: string;
  completedRounds: number;
  totalScore: number;
  failed: boolean;
  pointsPerRound: number[];
  isSessionComplete: boolean;
}

const WASM_BASE =
  (process.env.VITE_GENIUS_WASM_BASE ?? '/wasm/genius').replace(/\/$/, '');

let dotnetModulePromise: Promise<{
  getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
  getConfig: () => { mainAssemblyName: string };
}> | null = null;

async function loadWasmExports(): Promise<GeniusWasmExports> {
  if (!dotnetModulePromise) {
    dotnetModulePromise = (async () => {
      const dotnetUrl = `${WASM_BASE}/_framework/dotnet.js`;
      const bootConfigUrl = `${WASM_BASE}/_framework/blazor.boot.json`;
      const mod = await import(/* webpackIgnore: true */ dotnetUrl);
      const dotnet = mod.dotnet as
        | {
            withConfigSrc: (src: string) => { create: () => Promise<unknown> };
            create: () => Promise<unknown>;
          }
        | undefined;
      if (!dotnet?.create) {
        throw new Error('Genius WASM: dotnet runtime not found.');
      }
      const runtime = dotnet.withConfigSrc
        ? await dotnet.withConfigSrc(bootConfigUrl).create()
        : await dotnet.create();
      return runtime as {
        getAssemblyExports: (name: string) => Promise<Record<string, unknown>>;
        getConfig: () => { mainAssemblyName: string };
      };
    })();
  }

  const runtime = await dotnetModulePromise;
  const config = runtime.getConfig();
  return loadGeniusAssemblyExports(runtime.getAssemblyExports, config.mainAssemblyName);
}

export function useGeniusWasm(engineBootstrapCipher: string | undefined) {
  const exportsRef = useRef<GeniusWasmExports | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!engineBootstrapCipher?.trim()) {
      setReady(false);
      setError('Genius bootstrap is missing.');
      return;
    }

    setReady(false);
    setError(null);

    (async () => {
      try {
        const wasm = await loadWasmExports();
        if (cancelled) return;
        const keyB64 = wasm.getBootstrapKeyBase64();
        const plainB64 = await decryptGeniusBootstrapCipher(engineBootstrapCipher, keyB64);
        if (cancelled) return;
        const initResult = wasm.initializeSessionFromPlaintext(plainB64);
        const initError =
          typeof initResult === 'string' ? initResult.trim() : 'Failed to initialize Genius engine.';
        if (initError) {
          setError(initError);
          setReady(false);
          return;
        }
        exportsRef.current = wasm;
        setError(null);
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Genius WASM load failed.');
        setReady(false);
      }
    })();

    return () => {
      cancelled = true;
      exportsRef.current?.disposeSession();
      exportsRef.current = null;
      setReady(false);
    };
  }, [engineBootstrapCipher]);

  const submitRound = useCallback(
    async (picks: string[]): Promise<SubmitGeniusRoundResponse | null> => {
      const wasm = exportsRef.current;
      if (!wasm) return null;
      const json = wasm.submitRound(JSON.stringify(picks));
      return JSON.parse(json) as SubmitGeniusRoundResponse;
    },
    []
  );

  const getPlayState = useCallback(async (): Promise<GeniusPlayStateDto | null> => {
    const wasm = exportsRef.current;
    if (!wasm) return null;
    const json = wasm.getPlayState();
    return JSON.parse(json) as GeniusPlayStateDto;
  }, []);

  const buildFinishPayload = useCallback((): GeniusFinishAudit | null => {
    const wasm = exportsRef.current;
    if (!wasm) return null;
    const json = wasm.buildFinishPayload();
    if (!json || json === '{}') return null;
    return JSON.parse(json) as GeniusFinishAudit;
  }, []);

  const disposeSession = useCallback(() => {
    exportsRef.current?.disposeSession();
    exportsRef.current = null;
    setReady(false);
  }, []);

  return {
    ready,
    error,
    submitRound,
    getPlayState,
    buildFinishPayload,
    disposeSession,
  };
}
